import { type Observable } from 'rxjs';

/** Serviço que lista as solicitações em aberto de um serviço para uma UF. */
export interface ConsultaAbertosService {
    consultarAbertos(ufCrm: string): Observable<Record<string, unknown>[]>;
}

export interface ConsultaProtocoloRequest {
    numeroProtocolo: string;
    anoProtocolo: string;
    ufCrm: string;
}

/** Serviço que consulta um protocolo específico de solicitação. */
export interface ConsultaProtocoloService {
    consultar(params: ConsultaProtocoloRequest): Observable<unknown>;
}

/**
 * Assinatura estendida dos serviços de visto (corrido/fracionado): recebem o discriminador `codigoServico`
 * para diferenciar as duas modalidades no mesmo endpoint. Aplicada por narrowing só no ramo de visto.
 */
interface ConsultaVistoService {
    consultarAbertos(ufCrm: string, codigoServico: string): Observable<Record<string, unknown>[]>;
    consultar(params: ConsultaProtocoloRequest & { codigoServico: string }): Observable<unknown>;
}

export interface ServicosAbertosPorChave {
    areaAtuacao: ConsultaAbertosService & ConsultaProtocoloService;
    averbacaoNacionalidade: ConsultaAbertosService & ConsultaProtocoloService;
    averbacaoNome: ConsultaAbertosService & ConsultaProtocoloService;
    cancelamentoFalecimento: ConsultaAbertosService & ConsultaProtocoloService;
    carteiraProfissional: ConsultaAbertosService & ConsultaProtocoloService;
    cedulaIdentidade: ConsultaAbertosService & ConsultaProtocoloService;
    especialidadeMedica: ConsultaAbertosService & ConsultaProtocoloService;
    vistoCorrido: ConsultaAbertosService & ConsultaProtocoloService;
    preMedico: ConsultaAbertosService & ConsultaProtocoloService;
    cancelamentoInscricao: ConsultaAbertosService & ConsultaProtocoloService;
    consultorioPrivado: ConsultaAbertosService & ConsultaProtocoloService;
    medicoPcmso: ConsultaAbertosService & ConsultaProtocoloService;
    posGraduacao: ConsultaAbertosService & ConsultaProtocoloService;
    regimentoInterno: ConsultaAbertosService & ConsultaProtocoloService;
    comissaoEtica: ConsultaAbertosService & ConsultaProtocoloService;
    alteracaoCadastralPessoaJuridica: ConsultaAbertosService & ConsultaProtocoloService;
    cancelamentoPj: ConsultaAbertosService & ConsultaProtocoloService;
    diretorClinico: ConsultaAbertosService & ConsultaProtocoloService;
    solicitacaoDiretor: ConsultaAbertosService & ConsultaProtocoloService;
    prePrestador: ConsultaAbertosService & ConsultaProtocoloService;
    renovacaoCadastral: ConsultaAbertosService & ConsultaProtocoloService;
    solicitacaoDiretorTecnico: ConsultaAbertosService & ConsultaProtocoloService;
    cartEvento: ConsultaAbertosService & ConsultaProtocoloService;
}

const CODIGO_SERVICO_POR_CHAVE: Record<string, keyof ServicosAbertosPorChave> = {
    'area-atuacao': 'areaAtuacao',
    'area-de-atuacao': 'areaAtuacao',
    'averbacao-nacionalidade': 'averbacaoNacionalidade',
    'averbacao-nome': 'averbacaoNome',
    'cancelamento-falecimento': 'cancelamentoFalecimento',
    'cancelar-por-falecimento': 'cancelamentoFalecimento',
    'solicitar-2via-carteira': 'carteiraProfissional',
    'carteira-profissional': 'carteiraProfissional',
    'solicitar-2via-cedula-identidade': 'cedulaIdentidade',
    'cedula-de-identidade': 'cedulaIdentidade',
    especialidade: 'especialidadeMedica',
    especialidades: 'especialidadeMedica',
    'visto-corrido': 'vistoCorrido',
    'visto-fracionado': 'vistoCorrido',
    'pre-medico': 'preMedico',
    'cancelamento-inscricao': 'cancelamentoInscricao',
    'cancelamento-de-inscricao': 'cancelamentoInscricao',
    'consultorio-privado': 'consultorioPrivado',
    'cadastro-consultorio-privado': 'consultorioPrivado',
    'consultorio-privado-renovacao': 'consultorioPrivado',
    'renovacao-consultorio-privado': 'consultorioPrivado',
    'consultorio-privado-cancelamento': 'consultorioPrivado',
    'cancelamento-consultorio-privado': 'consultorioPrivado',
    pcmso: 'medicoPcmso',
    'responsavel-pcmso': 'medicoPcmso',
    pos: 'posGraduacao',
    'pos-graduacao': 'posGraduacao',
    'regimento-interno': 'regimentoInterno',
    'regimento-interno-corpo-clinico': 'regimentoInterno',
    'comissao-etica': 'comissaoEtica',
    'cancelamento-pj': 'cancelamentoPj',
    'visto-alteracao-contratual': 'alteracaoCadastralPessoaJuridica',
    'alteracao-cadastral': 'alteracaoCadastralPessoaJuridica',
    'alteracao-contratual': 'alteracaoCadastralPessoaJuridica',
    'diretor-clinico': 'diretorClinico',
    'diretoria-clinica': 'diretorClinico',
    'diretor-tecnico-solicitacao-pj': 'solicitacaoDiretor',
    'saida-diretor-tecnico': 'solicitacaoDiretor',
    'pre-prestador': 'prePrestador',
    'renovacao-cadastral': 'renovacaoCadastral',
    renovacao: 'renovacaoCadastral',
    'diretor-tecnico-solicitacao-pf': 'solicitacaoDiretorTecnico',
    'solicitacao-diretor-tecnico': 'solicitacaoDiretorTecnico',
    'cart-evento': 'cartEvento'
};

export function consultarAbertosPorCodigoServico(codigoServico: string, ufCrm: string, servicos: ServicosAbertosPorChave): Observable<Record<string, unknown>[]> {
    const chaveServico = CODIGO_SERVICO_POR_CHAVE[codigoServico];
    if (!chaveServico) {
        throw new Error(`Serviço sem endpoint de abertos mapeado: ${codigoServico}`);
    }

    if (codigoServico === 'visto-corrido' || codigoServico === 'visto-fracionado') {
        return (servicos[chaveServico] as ConsultaVistoService).consultarAbertos(ufCrm, codigoServico);
    }
    return servicos[chaveServico].consultarAbertos(ufCrm);
}

export function consultarProtocoloPorCodigoServico(codigoServico: string, request: ConsultaProtocoloRequest, servicos: ServicosAbertosPorChave): Observable<unknown> {
    const chaveServico = CODIGO_SERVICO_POR_CHAVE[codigoServico];
    if (!chaveServico) {
        throw new Error(`Serviço sem endpoint de consulta mapeado: ${codigoServico}`);
    }

    if (codigoServico === 'visto-corrido' || codigoServico === 'visto-fracionado') {
        return (servicos[chaveServico] as ConsultaVistoService).consultar({ ...request, codigoServico });
    }
    return servicos[chaveServico].consultar(request);
}
